import { SITE } from './site-config';

type StatsKey = 'collaborators' | 'users' | 'businesses';

interface StatsPayload {
  collaborators?: number;
  users?: number;
  businesses?: number;
  collaboratorCount?: number;
  userCount?: number;
  businessCount?: number;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function normalizePayload(raw: unknown): Partial<Record<StatsKey, number>> | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as StatsPayload;
  const out: Partial<Record<StatsKey, number>> = {};
  const c =
    readNumber(o.collaborators) ??
    readNumber(o.collaboratorCount);
  const u =
    readNumber(o.users) ??
    readNumber(o.userCount);
  const b =
    readNumber(o.businesses) ??
    readNumber(o.businessCount);
  if (c != null) {
    out.collaborators = Math.round(c);
  }
  if (u != null) {
    out.users = Math.round(u);
  }
  if (b != null) {
    out.businesses = Math.round(b);
  }
  return Object.keys(out).length ? out : null;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(value);
}

/**
 * Si existe `VITE_STATS_JSON_URL`, intenta sobrescribir las cifras del DOM.
 * El JSON puede usar `collaborators` / `users` / `businesses` o sufijos `Count`.
 */
export async function hydratePublicStats(): Promise<void> {
  const url = import.meta.env.VITE_STATS_JSON_URL?.trim();
  if (!url) {
    return;
  }

  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    if (!res.ok) {
      return;
    }
    const json: unknown = await res.json();
    const parsed = normalizePayload(json);
    if (!parsed) {
      return;
    }

    const fallback = SITE.statsFallback;
    const collaborators = parsed.collaborators ?? fallback.collaborators;
    const users = parsed.users ?? fallback.users;
    const businesses = parsed.businesses ?? fallback.businesses;

    const map: Record<StatsKey, number> = {
      collaborators,
      users,
      businesses,
    };

    for (const key of Object.keys(map) as StatsKey[]) {
      const el = document.querySelector(`[data-stat-value="${key}"]`);
      if (el) {
        el.textContent = formatCount(map[key]);
      }
    }
  } catch {
    /* red o CORS: se mantienen valores fallback ya pintados */
  }
}
